/**
 * MatrixPyGrid — Previous Year ageing-matrix entry grid.
 *
 * Slim sibling of `MultiColumnClassTable`'s CY render: same row + column
 * structure, but reads/writes `prior_column_values` instead of
 * `column_values`. Used as the body of the PY `AgeingSectionBand` on
 * any multi-column Note whose schedule has
 * `meta_data.display_options.py_layout = "separate_grid"` (today: Trade
 * Payables; future: other ageing matrices).
 *
 * Behaviour mirrors CY:
 *   • Skip classes flagged `meta_data.hide_in_main_note: true` (TB
 *     classification leaves, not matrix rows).
 *   • Render input rows as NumericInputs (cell editor binds to
 *     prior_column_values via setClassField).
 *   • Render computed-subtotal rows (computation_rule.type = SUM with
 *     operands) as read-only — values come from a synth dict that sums
 *     operands' prior_column_values per column.
 *   • Persist synth dicts into prior_column_values via a useEffect so
 *     the save flow + PDF render path see the same values.
 *   • Total column (is_computed + formula) auto-evaluates per row,
 *     including __rounded companion via the same dual-eval rule
 *     recomputeRow uses (autoRound for actuals-mode edits; formula
 *     against rounded operands for rounded-mode edits).
 *
 * Deferred (next iteration):
 *   • Reconciliation chip against TB opening sum (Σ live.py on hidden
 *     classification leaves with sign-flip for credit-balance schedules).
 *   • Action menus (hide_in_print, remarks). Not relevant for a
 *     presentation-only PY transcription grid.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { autoRound } from '../../shared/rounding';
import { NumericInput } from '../../shared/NumericInput';
import { evaluateFormula } from './formulaEvaluator';
import { ROUNDED_SUFFIX } from './noteClassDataResolver';
import type {
  NoteClassLive,
  NoteClassData,
  NoteWorkspaceData,
} from 'api/types/fsa/noteData.types';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';

interface Props {
  columnSpec: FormatColumnSpec[];
  classes: NoteClassLive[];
  workspace: NoteWorkspaceData;
  setClassField: <K extends keyof NoteClassData>(
    classCode: string,
    field: K,
    value: NoteClassData[K],
  ) => void;
  roundingFactorKey: string;
  displayMode: 'actual' | 'rounded';
  readOnly: boolean;
}

interface PyRow {
  classCode: string;
  label: string;
  isComputedSubtotal: boolean;
  computedOperands?: string[];
}

const MatrixPyGrid: React.FC<Props> = ({
  columnSpec,
  classes,
  workspace,
  setClassField,
  roundingFactorKey,
  displayMode,
  readOnly,
}) => {
  const isRounded = displayMode === 'rounded';
  const visibleColumns = useMemo(
    () => columnSpec.filter(c => !c.is_grouping_field),
    [columnSpec],
  );

  // ── Row build ────────────────────────────────────────────────────────────
  const rows = useMemo<PyRow[]>(() => {
    const out: PyRow[] = [];
    for (const c of classes) {
      // Skip classification leaves hidden from the matrix render.
      let hide = false;
      let isComputedSubtotal = false;
      let operands: string[] | undefined;
      if (c.metaDataJson) {
        try {
          const m = JSON.parse(c.metaDataJson) as { hide_in_main_note?: boolean };
          hide = m.hide_in_main_note === true;
        } catch { /* ignore */ }
      }
      if (hide) continue;
      if (c.isComputed && c.computationRuleJson) {
        try {
          const rule = JSON.parse(c.computationRuleJson) as { type?: string; operands?: string[] };
          if (rule.type === 'SUM' && Array.isArray(rule.operands)) {
            isComputedSubtotal = true;
            operands = rule.operands;
          }
        } catch { /* ignore */ }
      }
      out.push({
        classCode: c.classCode,
        label: c.className,
        isComputedSubtotal,
        computedOperands: operands,
      });
    }
    return out.sort((a, b) => {
      const ca = classes.find(c => c.classCode === a.classCode)?.displayOrder ?? 0;
      const cb = classes.find(c => c.classCode === b.classCode)?.displayOrder ?? 0;
      return ca - cb;
    });
  }, [classes]);

  // ── Subtotal synth (dual-mode mirror of CY) ──────────────────────────────
  // For each computed-subtotal row, sum operands' input-column values per
  // column. We sum BOTH the base (actuals) key and the __rounded
  // companion separately so the subtotal works whether the auditor typed
  // in actuals mode (only base populated → derive rounded via autoRound)
  // OR in rounded mode (only __rounded populated → keep the rounded sum,
  // back-derive actual via inverse-round when needed for the formula
  // operands). The 'whichever is non-zero wins' rule mirrors the
  // recomputeRow Phase B dual-eval shipped for the row's own Total.
  //
  // Fixed-point iteration handles chains (Grand Total = Subtotal MSME +
  // Subtotal Others).
  const subtotalSynth = useMemo<Map<string, Record<string, number>>>(() => {
    const computedRows = rows.filter(r => r.isComputedSubtotal && r.computedOperands?.length);
    if (computedRows.length === 0) return new Map();
    const amountCols = visibleColumns.filter(c => c.data_type === 'amount');
    const inputAmountCols = amountCols.filter(c => !c.is_computed);
    const computedAmountCols = amountCols.filter(c => c.is_computed && c.formula);

    const synth = new Map<string, Record<string, number>>();
    /** Read one operand's base (actuals) value for a given column. */
    const getOperandBase = (code: string, colCode: string): number => {
      const fromSynth = synth.get(code);
      if (fromSynth) return typeof fromSynth[colCode] === 'number' ? fromSynth[colCode] : 0;
      const pcv = workspace.class_data?.[code]?.prior_column_values;
      if (pcv && typeof pcv[colCode] === 'number') return pcv[colCode];
      return 0;
    };
    /** Read one operand's __rounded companion for a given column. */
    const getOperandRounded = (code: string, colCode: string): number => {
      const k = colCode + ROUNDED_SUFFIX;
      const fromSynth = synth.get(code);
      if (fromSynth) return typeof fromSynth[k] === 'number' ? fromSynth[k] : 0;
      const pcv = workspace.class_data?.[code]?.prior_column_values;
      if (pcv && typeof pcv[k] === 'number') return pcv[k];
      return 0;
    };

    let changed = true;
    let iter = 0;
    while (changed && iter < 10) {
      changed = false;
      iter += 1;
      for (const row of computedRows) {
        const next: Record<string, number> = {};
        // Input columns: sum BOTH base and rounded across operands. Pick
        // non-zero for the __rounded companion so rounded-mode-only edits
        // also flow through. Base stays the actuals sum (may be 0 if
        // auditor only typed in rounded mode — that's fine; the Total
        // column's formula will pick up the rounded operand path).
        for (const col of inputAmountCols) {
          let sumBase = 0;
          let sumRounded = 0;
          for (const op of row.computedOperands ?? []) {
            sumBase    += getOperandBase(op,    col.code);
            sumRounded += getOperandRounded(op, col.code);
          }
          const baseV    = Math.round(sumBase    * 100) / 100;
          const roundedV = Math.round(sumRounded * 100) / 100;
          next[col.code] = baseV;
          // Prefer the operands' summed rounded values; fall back to
          // autoRound(base) when operands had actuals but no rounded
          // companion (typical actuals-mode edits).
          next[col.code + ROUNDED_SUFFIX] = roundedV !== 0 ? roundedV : autoRound(baseV, roundingFactorKey);
        }
        // Computed columns (Total): evaluate the formula against base
        // operands AND rounded operands; pick non-zero for the rounded
        // companion. Mirrors recomputeRow Phase B.
        const roundedOperandsView: Record<string, number> = {};
        for (const c of inputAmountCols) {
          const r = next[c.code + ROUNDED_SUFFIX];
          if (typeof r === 'number' && Number.isFinite(r)) roundedOperandsView[c.code] = r;
        }
        for (const col of computedAmountCols) {
          try {
            const baseResult    = evaluateFormula(col.formula as string, next);
            const roundedResult = evaluateFormula(col.formula as string, roundedOperandsView);
            const v = typeof baseResult === 'number' ? Math.round(baseResult * 100) / 100 : 0;
            next[col.code] = v;
            const fromActual = autoRound(v, roundingFactorKey);
            next[col.code + ROUNDED_SUFFIX] = fromActual !== 0
              ? fromActual
              : Math.round((roundedResult ?? 0) * 100) / 100;
          } catch { /* ignore */ }
        }
        const prev = synth.get(row.classCode);
        // Compare both base and __rounded keys for change detection — a
        // rounded-only change without a base change still needs persistence.
        const hasChange = !prev
          || amountCols.some(c => prev[c.code] !== next[c.code])
          || amountCols.some(c => prev[c.code + ROUNDED_SUFFIX] !== next[c.code + ROUNDED_SUFFIX]);
        if (hasChange) {
          synth.set(row.classCode, next);
          changed = true;
        }
      }
    }
    return synth;
  }, [workspace.class_data, rows, visibleColumns, roundingFactorKey]);

  // ── Persistence ──────────────────────────────────────────────────────────
  // Write synth values into workspace.class_data[code].prior_column_values
  // so the save flow + PDF render path read the same values the auditor
  // sees. NEVER write amount_actual / prior_amount_actual — matrix rows
  // are presentation-only (same rule as CY subtotal persistence).
  useEffect(() => {
    if (subtotalSynth.size === 0) return;
    for (const [code, cv] of subtotalSynth) {
      const existing = workspace.class_data?.[code];
      const existingPcv = existing?.prior_column_values ?? null;
      let same = existingPcv != null;
      if (same && existingPcv) {
        for (const k of Object.keys(cv)) {
          if (existingPcv[k] !== cv[k]) { same = false; break; }
        }
      }
      if (same) continue;
      const nextPcv = { ...(existingPcv ?? {}), ...cv };
      setClassField(code, 'prior_column_values', nextPcv);
    }
  }, [subtotalSynth, workspace.class_data, setClassField]);

  // ── Per-row recompute (input rows only — mirrors recomputeRow on CY) ─────
  const recomputePyRow = useCallback((
    current: Record<string, number> | null | undefined,
    changedCode: string,
    changedValue: number,
  ): Record<string, number> => {
    const next: Record<string, number> = { ...(current ?? {}) };
    next[changedCode] = changedValue;
    const isRoundedEdit = changedCode.endsWith(ROUNDED_SUFFIX);
    const editedActualCode = isRoundedEdit
      ? changedCode.slice(0, -ROUNDED_SUFFIX.length)
      : null;
    // Phase A — refresh __rounded companions for non-computed amount columns.
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      if (col.is_computed) continue;
      const actual = next[col.code];
      if (typeof actual !== 'number') continue;
      const roundedKey = col.code + ROUNDED_SUFFIX;
      if (changedCode !== roundedKey) {
        next[roundedKey] = autoRound(actual, roundingFactorKey);
      }
    }
    // Rounded-operand view for dual-mode formula evaluation.
    const roundedOperands: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (col.data_type !== 'amount') continue;
      const r = next[col.code + ROUNDED_SUFFIX];
      if (typeof r === 'number' && Number.isFinite(r)) {
        roundedOperands[col.code] = r;
      }
    }
    // Phase B — evaluate computed columns.
    for (const col of visibleColumns) {
      if (!col.is_computed || !col.formula) continue;
      if (editedActualCode === col.code) continue;
      const v = evaluateFormula(col.formula, next);
      next[col.code] = v;
      if (col.data_type === 'amount') {
        const fromActual = autoRound(v, roundingFactorKey);
        const fromRounded = evaluateFormula(col.formula, roundedOperands);
        next[col.code + ROUNDED_SUFFIX] = fromActual !== 0 ? fromActual : fromRounded;
      }
    }
    return next;
  }, [visibleColumns, roundingFactorKey]);

  const handleCellChange = useCallback((classCode: string, colCode: string, value: number) => {
    const cd = workspace.class_data?.[classCode];
    const next = recomputePyRow(cd?.prior_column_values, colCode, value);
    setClassField(classCode, 'prior_column_values', next);
  }, [workspace.class_data, recomputePyRow, setClassField]);

  // ── Render ───────────────────────────────────────────────────────────────
  // Header rows: two-row layout when any column declares a `group`,
  // otherwise single-row. Mirrors CY header shape.
  const groupedCols = useMemo(() => {
    type Header =
      | { kind: 'col'; col: FormatColumnSpec; rowSpan: 2 }
      | { kind: 'group'; label: string; colCount: number };
    const out: Header[] = [];
    let i = 0;
    while (i < visibleColumns.length) {
      const c = visibleColumns[i];
      if (!c.group) {
        out.push({ kind: 'col', col: c, rowSpan: 2 });
        i++;
        continue;
      }
      let j = i;
      while (j < visibleColumns.length && visibleColumns[j].group === c.group) j++;
      out.push({ kind: 'group', label: c.group, colCount: j - i });
      i = j;
    }
    return out;
  }, [visibleColumns]);
  const hasGroupHeaders = groupedCols.some(h => h.kind === 'group');

  return (
    <div style={tableScrollStyle}>
      <table style={tableStyle}>
        <thead>
          {hasGroupHeaders ? (
            <>
              <tr>
                <th style={{ ...thLabelStyle, padding: '6px 10px' }} rowSpan={2}>Particulars</th>
                {groupedCols.map((h, i) => {
                  if (h.kind === 'col') {
                    return (
                      <th key={`r-${i}`} rowSpan={2} style={thAmountStyle}>
                        {h.col.label}
                      </th>
                    );
                  }
                  return (
                    <th key={`g-${i}`} colSpan={h.colCount} style={thGroupStyle}>
                      {h.label}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {visibleColumns.map(c => {
                  if (!c.group) return null;
                  return (
                    <th key={c.code} style={thAmountStyle}>
                      {c.label}
                    </th>
                  );
                })}
              </tr>
            </>
          ) : (
            <tr>
              <th style={{ ...thLabelStyle, padding: '6px 10px' }}>Particulars</th>
              {visibleColumns.map(c => (
                <th key={c.code} style={thAmountStyle}>{c.label}</th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map(row => {
            const cd = workspace.class_data?.[row.classCode];
            // Effective cv: subtotal rows use synth; input rows use saved.
            const cv = row.isComputedSubtotal
              ? (subtotalSynth.get(row.classCode) ?? cd?.prior_column_values ?? null)
              : (cd?.prior_column_values ?? null);
            return (
              <tr key={row.classCode} style={row.isComputedSubtotal ? subtotalRowStyle : rowStyle}>
                <td style={labelTdStyle}>{row.label}</td>
                {visibleColumns.map(col => {
                  if (col.data_type !== 'amount') {
                    return <td key={col.code} style={amountTdStyle}>{cv?.[col.code] ?? ''}</td>;
                  }
                  const actualKey = col.code;
                  const roundedKey = col.code + ROUNDED_SUFFIX;
                  const actualVal = cv?.[actualKey] ?? 0;
                  const roundedVal = cv?.[roundedKey];
                  const displayValue = isRounded
                    ? (roundedVal != null ? roundedVal : autoRound(actualVal, roundingFactorKey))
                    : actualVal;
                  const editTargetCode = isRounded ? roundedKey : actualKey;
                  // Lock rule mirrors CY: read-only on subtotal rows AND on
                  // is_computed columns AND in actuals mode for is_total cols.
                  const isFormulaDriven = (col.is_computed && col.formula) || col.drift_formula;
                  const cellIsReadOnly = readOnly
                    || row.isComputedSubtotal
                    || !!isFormulaDriven;
                  return (
                    <td key={col.code} style={amountTdStyle}>
                      {cellIsReadOnly ? (
                        <span style={amountReadOnlyStyle}>{fmtAmt(displayValue)}</span>
                      ) : (
                        <NumericInput
                          className="form-input"
                          value={displayValue}
                          onChange={(v: number) => handleCellChange(row.classCode, editTargetCode, v)}
                          style={amountInputStyle}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const tableScrollStyle: React.CSSProperties = {
  overflowX: 'auto',
  padding: '6px 0',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
};
const thLabelStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color, #e2e8f0)',
  fontWeight: 600,
};
const thAmountStyle: React.CSSProperties = {
  textAlign: 'right',
  padding: '6px 10px',
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color, #e2e8f0)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const thGroupStyle: React.CSSProperties = {
  ...thAmountStyle,
  textAlign: 'center',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const rowStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #fff)',
};
const subtotalRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #f8fafc)',
  fontWeight: 600,
};
const labelTdStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const amountTdStyle: React.CSSProperties = {
  padding: '2px 4px',
  textAlign: 'right',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  whiteSpace: 'nowrap',
};
const amountReadOnlyStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 6px',
};
const amountInputStyle: React.CSSProperties = {
  width: '100%',
  minWidth: '80px',
  textAlign: 'right',
  fontSize: '0.8125rem',
  padding: '4px 6px',
};

export default MatrixPyGrid;
